import React, { useContext, useEffect, useState } from 'react';
import { LanguageContext } from '../../../lib/i18n/LanguageProvider';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { ApiBaseResponse, ResponseCode } from '../../../models/Common/ApiBaseResponse';
import { GetHelpTopicListByCompanyInfo } from '../../../services/AlbinaSoftComWebPortal';
import { GetHelpTopicListByCompanyInfoRequest } from '../../../models/Request/GetHelpTopicListByCompanyInfoRequest';
import { GetHelpTopicListByCompanyInfoResponse } from '../../../models/Response/GetHelpTopicListByCompanyInfoResponse';
import { FaEnvelope, FaLink, FaPhoneAlt, FaQuestionCircle } from 'react-icons/fa';

type LayoutContextType = {
  setPageTitle: (title: string) => void;
  setPageTitle2: (title2: string) => void;
  setPageTitle3: (title3: string) => void;
};

const Company: React.FC = () => {
  const context = useContext(LanguageContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const storedLang = localStorage.getItem('storedLang') || 'tr-TR';
  const [apiData, setApiData] = useState<GetHelpTopicListByCompanyInfoResponse | null>(null);
  const { setPageTitle, setPageTitle2, setPageTitle3 } = useOutletContext<LayoutContextType>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(''); // Search term state

  const id = searchParams.get('id') || "";
  const token = searchParams.get('t') || "";

  const missingParams: string[] = [];
  if (!id) missingParams.push('Company ID');
  if (!token) missingParams.push('Token');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (apiData) {
      const title = apiData ? `Yardım Portalı | ${apiData?.CompanyName}` : 'Yardım Portalı | Albinasoft A.Ş.';
      setPageTitle(title);
    }
  }, [apiData, setPageTitle]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const requestBody: GetHelpTopicListByCompanyInfoRequest = {
        CompanyId: parseInt(id || "0", 10),
        Token: token || ""
      };

      let response: ApiBaseResponse<GetHelpTopicListByCompanyInfoResponse> | null = await GetHelpTopicListByCompanyInfo(requestBody);

      if (response && response.Content && response.ResponseCode === ResponseCode.OK) {
        setApiData(response.Content);
      } else {
        console.warn("Yanıt beklenenden farklı:", response);
      }

    } catch (error: any) {
      console.error('API çağrısı başarısız:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!context) {
    return null;
  }

  const { translations } = context;

  if (missingParams.length > 0) {
    return (
      <div className="content-inner container-fluid pb-0">
        <div className="row">
          <div className="col-3"></div>
          <div className="col-md-6 col-12">
            <div className="card">
              <div className="card-header">
                <h3>Hatalı İşlem</h3>
              </div>
              <div className="card-body">
                <p>Eksik Parametreler:</p>
                <ul>
                  {missingParams.map(param => (
                    <li key={param}>{param}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-inner container-fluid pb-0">
        <div className="row">
          <div className="col-3"></div>
          <div className="col-md-6 col-12">
            <div className="card">
              <div className="card-header">
                <h3>Hatalı İşlem</h3>
              </div>
              <div className="card-body">
                <p>Hata:</p>
                {error}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loader simple-loader">
        <div className="loader-body"></div>
      </div>
    );
  }

  // Filter topics based on search term
  const filteredTopics = apiData?.TopicList.filter(topic =>
    topic.HelpTopicHeader.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Web URL'sini kontrol et ve gerekirse https:// ekle
  const formattedWebUrl = apiData?.WebUrl && !/^https?:\/\//i.test(apiData.WebUrl)
    ? `https://${apiData.WebUrl}`
    : apiData?.WebUrl;

  return (
    <div>
      <Helmet>
        <title>
          {apiData ? `Yardım Portalı | ${apiData?.CompanyName}` : 'Yardım Portalı | Albinasoft A.Ş.'}
        </title>
        <meta name="description" content={apiData ? `${apiData?.CompanyName} Yardım Portalı` : 'Yardım Portalı'} />

        <meta property="og:title" content={apiData ? `${apiData?.CompanyName}` : ''} />
        <meta property="og:description" content={apiData ? `${apiData?.CompanyName} Yardım Portalı` : 'Yardım Portalı'} />
        <meta property="og:url" content={window.location.origin} />
      </Helmet>
      <div className="content-inner container-fluid pb-0">

        <div className="row mb-3">
          <div className="col-12">
            <input
              type="text"
              className="form-control"
              placeholder="Yardım konularında ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <div className="help-portal-widget">
                  <div>
                    <FaQuestionCircle size={30} />
                  </div>
                  <div className="help-portal-widget-detail">
                    <p className="mb-2">Yardım Konuları</p>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {filteredTopics && filteredTopics.length > 0 ? (
                  <div className="list-group">
                    <div className="row">
                      {filteredTopics.map((topic) => (
                        <div
                          key={topic.Id}
                          className="col-lg-6 col-12 mb-3">
                          <div
                            className="list-group-item list-group-item-action list-group-item-primary"
                            onClick={() => navigate(`/${storedLang}/Topic?id=${topic.Id}&t=${topic.Token}`)}
                            style={{ cursor: 'pointer', borderRadius: '0.5rem' }}
                          >
                            {topic.HelpTopicHeader}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p>Arama kriterine uygun yardım konusu bulunmamaktadır.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-4 col-12">
            <div className="card">
              <div className="card-body">
                <div className="help-portal-widget">
                  <div>
                    <FaPhoneAlt size={30} />
                  </div>
                  <div className="help-portal-widget-detail">
                    <p className="mb-2">Telefon</p>
                    <h4>
                      {apiData?.PhoneNo ? (
                        <a href={`tel:${apiData.PhoneNo}`}>
                          {apiData.PhoneNo}
                        </a>
                      ) : (
                        "Telefon numarası mevcut değil."
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-12">
            <div className="card">
              <div className="card-body">
                <div className="help-portal-widget">
                  <div>
                    <FaEnvelope size={30} />
                  </div>
                  <div className="help-portal-widget-detail">
                    <p className="mb-2">E-Posta</p>
                    <h4>
                      {apiData?.eMail ? (
                        <a href={`mailto:${apiData.eMail}`}>
                          {apiData.eMail}
                        </a>
                      ) : (
                        <p>E-posta bilgisi mevcut değil.</p>
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-12">
            <div className="card">
              <div className="card-body">
                <div className="help-portal-widget">
                  <div>
                    <FaLink size={30} />
                  </div>
                  <div className="help-portal-widget-detail">
                    <p className="mb-2">Web Sayfası</p>
                    <h4>
                      {formattedWebUrl ? (
                        <a href={formattedWebUrl} target="_blank" rel="noopener noreferrer">
                          {formattedWebUrl}
                        </a>
                      ) : (
                        <p>Web sitesi bilgisi mevcut değil.</p>
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Company;
